import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { RootState } from '../redux/store';
import { CalendarCheck, ExternalLink, Clock } from 'lucide-react';
import { SyncLoader } from 'react-spinners';
import { Button } from './Button';
import { resetAppointment } from '../redux/slices/appointmentSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePolling } from '../hooks/usePolling';
import { fetchRequestStatus } from '../services/api';
import { formatSelectedSlot } from '../utils/helpers';
import { REQUEST_POLL_INTERVAL_MS, REQUEST_POLL_TIMEOUT_MS } from '../utils/constants';

interface SuccessCardProps {
  className?: string;
  isSmartBooking?: boolean;
  success: { time: string; patientName: string; requestId: string | null };
}

const expiryText = (seconds: number | null) =>
  seconds == null ? 'shortly' : moment.duration(seconds, 'seconds').humanize(true);

export const SuccessCard = ({ className = '', isSmartBooking, success }: SuccessCardProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { practice } = useSelector((s: RootState) => s.auth);
  const isManual = practice?.scheduler_type === 'manual';

  // Poll until the portal link is ready, or we time out.
  const { status, data } = usePolling({
    enabled: !!success.requestId,
    fetcher: () => fetchRequestStatus(success.requestId!),
    isDone: (s) => !!s.portalLink,
    intervalMs: REQUEST_POLL_INTERVAL_MS,
    timeoutMs: REQUEST_POLL_TIMEOUT_MS,
  });

  const portalLink = data?.portalLink ?? null;
  const expirySeconds = data?.portalLinkExpirySeconds ?? null;

  // The magic link expires after `expirySeconds`; flip to an expired notice then.
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(false);
    if (!portalLink || expirySeconds == null) return;
    const id = setTimeout(() => setExpired(true), expirySeconds * 1000);
    return () => clearTimeout(id);
  }, [portalLink, expirySeconds]);

  const shell = `min-h-full space-y-4 py-4 flex flex-col items-center justify-center text-center ${className}`;
  const ready = !!portalLink && !expired;

  return (
    <div className={shell}>
      <CalendarCheck className="w-12 h-12 sm:w-14 sm:h-14 text-success-700" />
      {isManual ? (
        <div className="text-lg sm:text-xl font-semibold text-black w-full max-w-md">Thanks {success.patientName}!</div>
      ) : (
        <div className="space-y-1 w-full max-w-md">
          <div className="text-lg sm:text-xl font-normal text-black">
            {success.patientName}, we've got you<br />confirmed for your appointment
          </div>
          <div className="text-lg sm:text-xl font-bold text-black">{formatSelectedSlot(success.time)}</div>
        </div>
      )}
      <Button
        title={isManual ? 'Request another appointment' : 'Book another appointment'}
        variant="secondary"
        onClick={() => {
          dispatch(resetAppointment());
          navigate(isSmartBooking ? `../smart-booking${location.search || ''}` : '../step-1');
        }}
        className="shrink-0"
      />

      <div className="w-full max-w-md border-t border-gray-100" />

      {status === 'polling' ? (
        <>
          <SyncLoader size={10} />
          <div className="text-sm sm:text-base text-gray-600 w-full max-w-md">Please wait, we're getting your patient portal ready…</div>
        </>
      ) : ready ? (
        <>
          <div className="text-sm sm:text-base text-gray-600 w-full max-w-md">
            {isManual
              ? "We've received your appointment request. Open your patient portal to complete any other necessary details."
              : "Open your patient portal to complete any remaining details."}
          </div>
          <Button
            title={<span className="flex items-center gap-2"><ExternalLink size={18} /> Open patient portal</span>}
            onClick={() => window.open(portalLink!, '_blank', 'noopener,noreferrer')}
            className="shrink-0"
          />
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Clock size={14} />
            This link will expire {expiryText(expirySeconds)}.
          </div>
        </>
      ) : (
        <>
          <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-amber-500" />
          {expired && <div className="text-lg sm:text-xl font-semibold text-black w-full max-w-md">Your magic link has expired</div>}
          <div className="text-sm sm:text-base text-gray-600 w-full max-w-md">
            {expired
              ? 'Your patient portal link is no longer valid. Please book another appointment to continue.'
              : "This is taking a little longer than usual. We'll email you a link to your patient portal shortly."}
          </div>
        </>
      )}
    </div>
  );
};
